import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MessageSquare, Mail, Check, AlertCircle, Key, 
  Settings, Zap, Shield, CreditCard, ChevronRight,
  ExternalLink, Trash2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

export default function CommunicationSettings({ user }) {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [apiKeyStatus, setApiKeyStatus] = useState({ whatsapp: {}, email: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  
  // Form states
  const [whatsappConfig, setWhatsappConfig] = useState({
    phone_number_id: '',
    access_token: '',
    business_account_id: ''
  });
  
  const [emailConfig, setEmailConfig] = useState({
    api_key: '',
    from_email: '',
    from_name: ''
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, myPlanRes, keysRes] = await Promise.all([
        fetch(`${API_URL}/api/communication/plans`),
        fetch(`${API_URL}/api/communication/my-plan`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/communication/api-keys/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData.plans || []);
      }

      if (myPlanRes.ok) {
        const planData = await myPlanRes.json();
        setCurrentPlan(planData);
        setBillingCycle(planData.billing_cycle || 'monthly');
      }

      if (keysRes.ok) {
        const keysData = await keysRes.json();
        setApiKeyStatus(keysData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load communication settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/communication/select-plan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_type: planId,
          billing_cycle: billingCycle
        })
      });

      if (res.ok) {
        toast.success('Communication plan updated!');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to update plan');
      }
    } catch (error) {
      toast.error('Error updating plan');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWhatsApp = async () => {
    if (!whatsappConfig.phone_number_id || !whatsappConfig.access_token) {
      toast.error('Please fill in Phone Number ID and Access Token');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/communication/configure/whatsapp`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(whatsappConfig)
      });

      if (res.ok) {
        toast.success('WhatsApp API configured successfully!');
        setWhatsappConfig({ phone_number_id: '', access_token: '', business_account_id: '' });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save WhatsApp configuration');
      }
    } catch (error) {
      toast.error('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    if (!emailConfig.api_key || !emailConfig.from_email) {
      toast.error('Please fill in API Key and From Email');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/communication/configure/email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailConfig)
      });

      if (res.ok) {
        toast.success('Email API configured successfully!');
        setEmailConfig({ api_key: '', from_email: '', from_name: '' });
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to save Email configuration');
      }
    } catch (error) {
      toast.error('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveWhatsApp = async () => {
    if (!window.confirm('Are you sure you want to remove WhatsApp configuration?')) return;

    try {
      const res = await fetch(`${API_URL}/api/communication/api-keys/whatsapp`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('WhatsApp configuration removed');
        fetchData();
      }
    } catch (error) {
      toast.error('Error removing configuration');
    }
  };

  const handleRemoveEmail = async () => {
    if (!window.confirm('Are you sure you want to remove Email configuration?')) return;

    try {
      const res = await fetch(`${API_URL}/api/communication/api-keys/email`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Email configuration removed');
        fetchData();
      }
    } catch (error) {
      toast.error('Error removing configuration');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const byokPlan = plans.find(p => p.is_byok);
  const paidPlans = plans.filter(p => !p.is_byok);

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Communication Settings
          </CardTitle>
          <CardDescription>
            Configure WhatsApp and Email for your spa notifications and campaigns
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* WhatsApp Status */}
            <div className={`p-4 rounded-lg border ${
              apiKeyStatus.whatsapp?.configured 
                ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
            }`}>
              <div className="flex items-center gap-3">
                <MessageSquare className={`w-8 h-8 ${
                  apiKeyStatus.whatsapp?.configured ? 'text-green-600' : 'text-amber-600'
                }`} />
                <div>
                  <h4 className="font-semibold">WhatsApp Business API</h4>
                  {apiKeyStatus.whatsapp?.configured ? (
                    <p className="text-sm text-green-600">Configured & Active</p>
                  ) : (
                    <p className="text-sm text-amber-600">Not Configured</p>
                  )}
                </div>
              </div>
            </div>

            {/* Email Status */}
            <div className={`p-4 rounded-lg border ${
              apiKeyStatus.email?.configured 
                ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
            }`}>
              <div className="flex items-center gap-3">
                <Mail className={`w-8 h-8 ${
                  apiKeyStatus.email?.configured ? 'text-green-600' : 'text-amber-600'
                }`} />
                <div>
                  <h4 className="font-semibold">Email (Resend)</h4>
                  {apiKeyStatus.email?.configured ? (
                    <p className="text-sm text-green-600">Configured • {apiKeyStatus.email.from_email}</p>
                  ) : (
                    <p className="text-sm text-amber-600">Not Configured</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="plans" className="space-y-6">
        <TabsList>
          <TabsTrigger value="plans">
            <CreditCard className="w-4 h-4 mr-2" /> Communication Plans
          </TabsTrigger>
          <TabsTrigger value="byok">
            <Key className="w-4 h-4 mr-2" /> Bring Your Own Keys
          </TabsTrigger>
        </TabsList>

        {/* Plans Tab */}
        <TabsContent value="plans">
          <Card>
            <CardHeader>
              <CardTitle>Choose Your Communication Plan</CardTitle>
              <CardDescription>
                Select a plan or use your own API keys (BYOK) for unlimited usage
              </CardDescription>
              
              {/* Billing Toggle */}
              <div className="flex items-center gap-4 mt-4">
                <span className={billingCycle === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}>
                  Monthly
                </span>
                <button
                  onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    billingCycle === 'annual' ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
                <span className={billingCycle === 'annual' ? 'font-semibold' : 'text-muted-foreground'}>
                  Annual <Badge variant="secondary" className="ml-1">Save ~17%</Badge>
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* BYOK Option */}
                {byokPlan && (
                  <Card className={`border-2 ${
                    currentPlan?.plan?.is_byok ? 'border-primary bg-primary/5' : 'border-muted'
                  }`}>
                    <CardContent className="pt-6">
                      <div className="flex items-center gap-2 mb-2">
                        <Key className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold">{byokPlan.name}</h3>
                      </div>
                      <p className="text-2xl font-bold">
                        {formatCurrency(billingCycle === 'monthly' ? byokPlan.monthly_price : byokPlan.annual_price)}
                        <span className="text-sm font-normal text-muted-foreground">
                          /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      </p>
                      {billingCycle === 'annual' && byokPlan.annual_savings > 0 && (
                        <p className="text-sm text-green-600 mb-2">
                          Save {formatCurrency(byokPlan.annual_savings)}/year
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground mb-4">{byokPlan.description}</p>
                      <ul className="space-y-2 mb-4">
                        {byokPlan.features.slice(0, 4).map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button 
                        variant={currentPlan?.plan?.is_byok ? "secondary" : "outline"}
                        className="w-full"
                        onClick={() => handleSelectPlan('byok')}
                        disabled={saving || currentPlan?.plan?.is_byok}
                      >
                        {currentPlan?.plan?.is_byok ? 'Current Plan' : 'Select BYOK'}
                      </Button>
                    </CardContent>
                  </Card>
                )}

                {/* Paid Plans */}
                {paidPlans.map((plan) => {
                  const price = billingCycle === 'monthly' ? plan.monthly_price : plan.annual_price;
                  const isCurrentPlan = currentPlan?.plan?.plan_id === plan.plan_id;
                  
                  return (
                    <Card key={plan.plan_id} className={`border-2 ${
                      isCurrentPlan ? 'border-primary bg-primary/5' : 'border-muted'
                    }`}>
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">{plan.name}</h3>
                        <p className="text-2xl font-bold">
                          {formatCurrency(price)}
                          <span className="text-sm font-normal text-muted-foreground">
                            /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                          </span>
                        </p>
                        {billingCycle === 'annual' && plan.annual_savings > 0 && (
                          <p className="text-sm text-green-600 mb-2">
                            Save {formatCurrency(plan.annual_savings)}/year
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
                        <ul className="space-y-2 mb-4">
                          {plan.features.slice(0, 4).map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm">
                              <Check className="w-4 h-4 text-green-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                        <Button 
                          variant={isCurrentPlan ? "secondary" : "default"}
                          className="w-full"
                          onClick={() => handleSelectPlan(plan.plan_id)}
                          disabled={saving || isCurrentPlan}
                        >
                          {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BYOK Configuration Tab */}
        <TabsContent value="byok">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* WhatsApp Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-green-600" />
                  WhatsApp Business API
                </CardTitle>
                <CardDescription>
                  Configure your WhatsApp Business API credentials
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {apiKeyStatus.whatsapp?.configured ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="flex items-center gap-2 text-green-600 mb-2">
                        <Check className="w-5 h-5" />
                        <span className="font-semibold">Configured</span>
                      </div>
                      <p className="text-sm">Phone ID: {apiKeyStatus.whatsapp.phone_number_id}</p>
                      <p className="text-sm text-muted-foreground">
                        Configured on: {new Date(apiKeyStatus.whatsapp.configured_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={handleRemoveWhatsApp}>
                      <Trash2 className="w-4 h-4 mr-2" /> Remove Configuration
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label>Phone Number ID *</Label>
                      <Input
                        value={whatsappConfig.phone_number_id}
                        onChange={(e) => setWhatsappConfig({...whatsappConfig, phone_number_id: e.target.value})}
                        placeholder="Enter your WhatsApp Phone Number ID"
                      />
                    </div>
                    <div>
                      <Label>Access Token *</Label>
                      <Input
                        type="password"
                        value={whatsappConfig.access_token}
                        onChange={(e) => setWhatsappConfig({...whatsappConfig, access_token: e.target.value})}
                        placeholder="Enter your permanent access token"
                      />
                    </div>
                    <div>
                      <Label>Business Account ID (Optional)</Label>
                      <Input
                        value={whatsappConfig.business_account_id}
                        onChange={(e) => setWhatsappConfig({...whatsappConfig, business_account_id: e.target.value})}
                        placeholder="Enter your Business Account ID"
                      />
                    </div>
                    <Button onClick={handleSaveWhatsApp} disabled={saving}>
                      {saving ? 'Saving...' : 'Save WhatsApp Configuration'}
                    </Button>
                    <a 
                      href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      How to get WhatsApp API credentials <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Email Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  Email API (Resend)
                </CardTitle>
                <CardDescription>
                  Configure your Resend API credentials for email communications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {apiKeyStatus.email?.configured ? (
                  <div className="space-y-4">
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="flex items-center gap-2 text-green-600 mb-2">
                        <Check className="w-5 h-5" />
                        <span className="font-semibold">Configured</span>
                      </div>
                      <p className="text-sm">From: {apiKeyStatus.email.from_name} &lt;{apiKeyStatus.email.from_email}&gt;</p>
                      <p className="text-sm text-muted-foreground">
                        Configured on: {new Date(apiKeyStatus.email.configured_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="destructive" size="sm" onClick={handleRemoveEmail}>
                      <Trash2 className="w-4 h-4 mr-2" /> Remove Configuration
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <Label>Resend API Key *</Label>
                      <Input
                        type="password"
                        value={emailConfig.api_key}
                        onChange={(e) => setEmailConfig({...emailConfig, api_key: e.target.value})}
                        placeholder="re_xxxxxxxxx"
                      />
                    </div>
                    <div>
                      <Label>From Email *</Label>
                      <Input
                        type="email"
                        value={emailConfig.from_email}
                        onChange={(e) => setEmailConfig({...emailConfig, from_email: e.target.value})}
                        placeholder="notifications@yourdomain.com"
                      />
                    </div>
                    <div>
                      <Label>From Name (Optional)</Label>
                      <Input
                        value={emailConfig.from_name}
                        onChange={(e) => setEmailConfig({...emailConfig, from_name: e.target.value})}
                        placeholder="Your Spa Name"
                      />
                    </div>
                    <Button onClick={handleSaveEmail} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Email Configuration'}
                    </Button>
                    <a 
                      href="https://resend.com/docs/introduction" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-primary hover:underline"
                    >
                      How to get Resend API key <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Usage Summary */}
      {currentPlan?.usage && !currentPlan?.plan?.is_byok && (
        <Card>
          <CardHeader>
            <CardTitle>Current Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">WhatsApp Messages</span>
                  <span className="text-sm text-muted-foreground">
                    {currentPlan.usage.whatsapp_used} / {currentPlan.usage.whatsapp_limit}
                  </span>
                </div>
                <div className="h-2 bg-background rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all"
                    style={{ width: `${Math.min(100, (currentPlan.usage.whatsapp_used / currentPlan.usage.whatsapp_limit) * 100)}%` }}
                  />
                </div>
                {currentPlan.usage.whatsapp_overage_count > 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    Overage: {currentPlan.usage.whatsapp_overage_count} messages ({formatCurrency(currentPlan.usage.whatsapp_overage_charge)})
                  </p>
                )}
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium">Emails</span>
                  <span className="text-sm text-muted-foreground">
                    {currentPlan.usage.emails_used} / {currentPlan.usage.email_limit}
                  </span>
                </div>
                <div className="h-2 bg-background rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 transition-all"
                    style={{ width: `${Math.min(100, (currentPlan.usage.emails_used / currentPlan.usage.email_limit) * 100)}%` }}
                  />
                </div>
                {currentPlan.usage.email_overage_count > 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    Overage: {currentPlan.usage.email_overage_count} emails ({formatCurrency(currentPlan.usage.email_overage_charge)})
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
